import { Icon } from "@design-system/Icon";
import { Button } from "@design-system/Inputs/Button";
import { Textarea } from "@design-system/Inputs/Textarea";
import { Box } from "@design-system/Layout/Box";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { cn } from "@design-system/Utilities";
import { QuestionProof } from "@generated/client/graphql";
import { ChangeEvent, type FC } from "react";
import { useTranslation } from "react-i18next";
import { keys, RESOURCE_NAME } from "../../i18n";
import { type Option } from "../../types";
import { ProofSelect } from "./ProofSelect";

export const OptionsBlock: FC<{
    opts: Option[];
    onChange: (opts: Option[]) => void;
    locale: string;
    withProof?: boolean;
}> = ({ opts = [], onChange, locale, withProof = false }) => {
    const { t } = useTranslation(RESOURCE_NAME);
    if (opts.length === 0) {
        opts = [{ name: { [locale]: "" }, proof: QuestionProof.NotAllowed }];
    }

    const addOption = () => {
        onChange([
            ...opts,
            { name: { [locale]: "" }, proof: QuestionProof.NotAllowed },
        ]);
    };

    const makeChangeHandler =
        (index: number) => (e: ChangeEvent<HTMLTextAreaElement>) => {
            onChange(
                opts.map((opt, i) =>
                    i === index
                        ? {
                              ...opt,
                              name: { ...opt.name, [locale]: e.target.value },
                          }
                        : opt,
                ),
            );
        };

    const makeDeleteHandler = (index: number) => () => {
        if (index === 0) return;
        onChange(opts.filter((_, i) => i !== index));
    };

    const makeProofChangeHandler =
        (index: number) => (proof: QuestionProof) => {
            onChange(
                opts.map((opt, i) => (i === index ? { ...opt, proof } : opt)),
            );
        };

    return (
        <FlexCol className="pl-4" gap="4">
            {
                <FlexCol gap="4">
                    {opts.map((opt, index) => (
                        <FlexRow
                            gap="2"
                            key={index}
                            alignItems="center"
                            w="full"
                        >
                            <FlexRow gap="2" w="full">
                                <Textarea
                                    placeholder={t(
                                        keys.create_questions_modal_options_block_label,
                                        {
                                            index: index + 1,
                                        },
                                    )}
                                    className="resize-y"
                                    rows={1}
                                    onChange={makeChangeHandler(index)}
                                    value={opt.name[locale] ?? ""}
                                    required
                                />
                                {withProof && (
                                    <Box className="shrink-0">
                                        <ProofSelect
                                            value={
                                                opts[index]?.proof ??
                                                QuestionProof.NotAllowed
                                            }
                                            onChange={makeProofChangeHandler(
                                                index,
                                            )}
                                        />
                                    </Box>
                                )}
                            </FlexRow>

                            <Button
                                variant="text"
                                size="sm"
                                onClick={makeDeleteHandler(index)}
                                disabled={index === 0}
                                className={cn(index === 0 && "invisible")}
                            >
                                <Icon name="trash" size="sm" />
                            </Button>
                        </FlexRow>
                    ))}
                </FlexCol>
            }
            <FlexRow alignSelf="start">
                <Button onClick={addOption} size="sm" variant="outlined">
                    <Button.Icon name="circlePlus" />

                    {t(keys.create_questions_modal_options_block_add_option)}
                </Button>
            </FlexRow>
        </FlexCol>
    );
};
